import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';

export interface SelfStudent {
  id: string;
  name: string;
  grade: string | null;
  school: string | null;
}

/**
 * 검사 결과를 저장/조회할 수 있도록 하는 훅.
 *
 * - 'student' 역할: 소속 학원의 students 레코드(user_id 연결)에 결과를 저장한다.
 * - 'member' 역할: '본인'을 가리키는 students 레코드를 자동 생성해 결과를 저장한다.
 *   (test_results.student_id 가 NOT NULL 이라 본인 레코드가 필요함)
 *
 * 두 경우 모두 결과에 user_id 를 함께 기록해, 개인별 조회(RLS)가 가능하도록 한다.
 * 원장/운영자는 검사 결과를 직접 저장하지 않는다.
 */
export function useTestResult() {
  const { profile } = useAuth();
  const [studentId, setStudentId] = useState<string | null>(null);
  const [student, setStudent] = useState<SelfStudent | null>(null);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  useEffect(() => {
    const role = profile?.role;
    if (role !== 'student' && role !== 'member') {
      setStudentId(null);
      setStudent(null);
      return;
    }
    let active = true;
    supabase
      .from('students')
      .select('id, name, grade, school')
      .eq('user_id', profile.id)
      .maybeSingle()
      .then(({ data }) => {
        if (!active) return;
        setStudentId(data?.id ?? null);
        setStudent(
          data
            ? { id: data.id, name: data.name, grade: data.grade, school: data.school }
            : null,
        );
      })
      .catch(() => {
        if (active) {
          setStudentId(null);
          setStudent(null);
        }
      });
    return () => {
      active = false;
    };
  }, [profile?.id, profile?.role]);

  const saveTestResult = useCallback(
    async (
      testType: string,
      title: string,
      score: number | null,
      result: Record<string, unknown>,
    ) => {
      if (!profile) return false;
      const role = profile.role;
      if (role !== 'student' && role !== 'member') return false;

      setSaveStatus('saving');
      try {
        // studentId가 아직 로드되지 않았다면(비동기 조회 전) 이 시점에 직접 조회
        let sid = studentId;
        if (!sid) {
          const { data } = await supabase
            .from('students')
            .select('id')
            .eq('user_id', profile.id)
            .maybeSingle();
          sid = data?.id ?? null;
        }

        // 레코드가 없다면(일반회원의 첫 검사 등) 본인 레코드를 자동 생성
        if (!sid) {
          const { data: created, error: createErr } = await supabase
            .from('students')
            .insert({
              name: profile.full_name || '사용자',
              user_id: profile.id,
              academy_id: profile.academy_id,
              grade: profile.grade ?? null,
            })
            .select('id, name, grade, school')
            .single();
          if (createErr) throw createErr;
          sid = created.id;
          setStudentId(created.id);
          setStudent({
            id: created.id,
            name: created.name,
            grade: created.grade,
            school: created.school,
          });
        }

        const { error } = await supabase.from('test_results').insert({
          student_id: sid,
          user_id: profile.id,
          academy_id: profile.academy_id,
          test_type: testType,
          title,
          score,
          result,
        });
        if (error) throw error;
        setSaveStatus('saved');
        return true;
      } catch (e) {
        setSaveStatus('error');
        console.error('검사 결과 저장 실패:', e);
        return false;
      }
    },
    [studentId, profile],
  );

  return { studentId, student, saveStatus, saveTestResult };
}